import React from 'react';
import { cn } from '../../utils/cn';

/**
 * Base Skeleton component
 */
export const Skeleton = ({ className, ...props }) => {
  return (
    <div
      className={cn(
        "animate-pulse rounded-md bg-slate-200",
        className
      )}
      {...props}
    />
  );
};

/**
 * Text skeleton - for single line text
 */
export const SkeletonText = ({ width = 'full', className }) => {
  const widthClass = {
    full: 'w-full',
    '3/4': 'w-3/4',
    '2/3': 'w-2/3',
    '1/2': 'w-1/2',
    '1/3': 'w-1/3',
    '1/4': 'w-1/4',
  };

  return (
    <Skeleton 
      className={cn(
        "h-4",
        widthClass[width] || width,
        className
      )} 
    />
  );
};

/**
 * Avatar skeleton - for circular avatars
 */
export const SkeletonAvatar = ({ size = 'md', className }) => {
  const sizeClass = {
    sm: 'h-8 w-8',
    md: 'h-10 w-10',
    lg: 'h-12 w-12',
    xl: 'h-16 w-16',
  };

  return (
    <Skeleton 
      className={cn(
        "rounded-full",
        sizeClass[size],
        className
      )} 
    />
  );
};

/**
 * Button skeleton
 */
export const SkeletonButton = ({ size = 'md', className }) => {
  const sizeClass = {
    sm: 'h-8 w-20',
    md: 'h-10 w-24',
    lg: 'h-12 w-32',
  };

  return (
    <Skeleton 
      className={cn(
        "rounded-lg",
        sizeClass[size],
        className
      )} 
    />
  );
};

/**
 * Card skeleton - for card-like containers
 */
export const SkeletonCard = ({ children, className }) => {
  return (
    <div className={cn(
      "rounded-xl border border-slate-200 bg-white p-6",
      className
    )}>
      {children}
    </div>
  );
};

/**
 * Table row skeleton
 */
export const SkeletonTableRow = ({ columns = 5, className }) => {
  return (
    <tr className={cn("border-b border-slate-100", className)}>
      {Array.from({ length: columns }).map((_, i) => (
        <td key={i} className="px-6 py-4">
          <SkeletonText width={i === 0 ? '3/4' : '2/3'} />
        </td>
      ))}
    </tr>
  );
};

/**
 * Table skeleton - for tables
 */
export const SkeletonTable = ({ rows = 5, columns = 5, className }) => {
  return (
    <div className={cn("overflow-hidden rounded-lg border border-slate-200", className)}>
      <table className="w-full">
        <thead className="bg-slate-50">
          <tr>
            {Array.from({ length: columns }).map((_, i) => (
              <th key={i} className="px-6 py-3">
                <SkeletonText width="2/3" className="h-3" />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, i) => (
            <SkeletonTableRow key={i} columns={columns} />
          ))}
        </tbody>
      </table>
    </div>
  );
};

/**
 * List item skeleton
 */
export const SkeletonListItem = ({ hasAvatar = true, lines = 2, className }) => {
  return (
    <div className={cn("flex items-start gap-4 py-3", className)}>
      {hasAvatar && <SkeletonAvatar />}
      <div className="flex-1 space-y-2">
        {Array.from({ length: lines }).map((_, i) => (
          <SkeletonText key={i} width={i === lines - 1 ? '1/2' : '3/4'} />
        ))}
      </div>
    </div>
  );
};

/**
 * Metric card skeleton (for dashboard)
 */
export const SkeletonMetricCard = ({ className }) => {
  return (
    <SkeletonCard className={className}>
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <SkeletonText width="1/3" className="h-3" />
          <Skeleton className="h-8 w-8 rounded-lg" />
        </div>
        <Skeleton className="h-8 w-24" />
        <SkeletonText width="1/2" className="h-3" />
      </div>
    </SkeletonCard>
  );
};

/**
 * Form field skeleton
 */
export const SkeletonFormField = ({ className }) => {
  return (
    <div className={cn("space-y-1.5", className)}>
      <SkeletonText width="1/4" className="h-3" />
      <Skeleton className="h-10 w-full rounded-lg" />
    </div>
  );
};

/**
 * Dashboard skeleton layout
 */
export const SkeletonDashboard = () => {
  return (
    <div className="space-y-6">
      {/* Metrics Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <SkeletonMetricCard key={i} />
        ))}
      </div>
      
      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SkeletonCard>
          <SkeletonText width="1/4" className="h-5 mb-4" />
          <Skeleton className="h-64 w-full rounded-lg" />
        </SkeletonCard>
        <SkeletonCard>
          <SkeletonText width="1/4" className="h-5 mb-4" />
          <Skeleton className="h-64 w-full rounded-lg" />
        </SkeletonCard>
      </div>
      
      {/* Table Section */}
      <SkeletonCard>
        <SkeletonText width="1/4" className="h-5 mb-4" />
        <SkeletonTable rows={5} columns={5} />
      </SkeletonCard>
    </div>
  );
};

/**
 * Vendor list skeleton layout
 */
export const SkeletonVendorList = () => {
  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex gap-4">
        <Skeleton className="h-10 w-64 rounded-lg" />
        <Skeleton className="h-10 w-32 rounded-lg" />
        <Skeleton className="h-10 w-32 rounded-lg" />
      </div>
      
      {/* Table */}
      <SkeletonTable rows={10} columns={7} />
      
      {/* Pagination */}
      <div className="flex justify-between items-center">
        <SkeletonText width="1/4" className="h-4" />
        <div className="flex gap-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-8 w-8 rounded-md" />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Skeleton;











